import { useState } from "react";
import { mutate } from "swr";
import { Loader2, X } from "lucide-react";

import { useInvitations } from "@/hooks/use-organization";
import { useOrganization } from "@/context/OrganizationContext";
import { OrganizationService } from "@/services/organization.service";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { InviteMemberDialog } from "./invite-member-dialog";

export function InvitationsTable() {
  const { membership } = useOrganization();
  const { invitations, isLoading, isError } = useInvitations();
  const [cancelingId, setCancelingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canManage = membership?.role === "owner" || membership?.role === "admin";

  const handleCancel = async (invitationId: string) => {
    setCancelingId(invitationId);
    setError(null);
    try {
      await OrganizationService.cancelInvitation(invitationId);
      mutate("organization-invitations");
    } catch (err: any) {
      setError(err?.message || "Failed to cancel the invitation.");
    } finally {
      setCancelingId(null);
    }
  };

  if (isError) {
    return <div className="p-6 text-red-500">Failed to load invitations</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between px-6 pt-6">
        <p className="text-sm text-muted-foreground">
          Invitations that have not been accepted yet.
        </p>
        {canManage && <InviteMemberDialog />}
      </div>

      {error && <p className="px-6 text-sm text-red-500">{error}</p>}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="pl-6">Email</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Invited by</TableHead>
            <TableHead>Expires</TableHead>
            <TableHead className="text-right pr-6">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={6} className="h-24 text-center">
                <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
              </TableCell>
            </TableRow>
          ) : invitations.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                No pending invitations.
              </TableCell>
            </TableRow>
          ) : (
            invitations.map((invitation) => (
              <TableRow key={invitation.id}>
                <TableCell className="pl-6 font-medium">{invitation.email}</TableCell>
                <TableCell className="capitalize">{invitation.role ?? "member"}</TableCell>
                <TableCell>
                  <Badge variant={invitation.status === "expired" ? "outline" : "secondary"}>
                    {invitation.status}
                  </Badge>
                </TableCell>
                <TableCell>{invitation.inviter.name}</TableCell>
                <TableCell>{new Date(invitation.expiresAt).toLocaleString()}</TableCell>
                <TableCell className="text-right pr-6">
                  {canManage && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive/90"
                      onClick={() => handleCancel(invitation.id)}
                      disabled={cancelingId === invitation.id}
                      title="Cancel invitation"
                    >
                      {cancelingId === invitation.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <X className="h-4 w-4" />
                      )}
                      <span className="sr-only">Cancel</span>
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
